/**
 * Dynamic Property Panel
 * 
 * Auto-generates property editing forms from Zod schemas
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  FormGroup,
  Checkbox,
  ListItemText,
  FormHelperText,
  Button,
  IconButton,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Image as ImageIcon,
  VideoFile as VideoIcon,
} from '@mui/icons-material';
import { z } from 'zod';
import { Block, ComponentType, Style } from '../../types/page-builder.types';
import { ComponentRegistry } from '../../utils/component-registry';
import { MediaUploadDialog } from './MediaUploadDialog';
import { PageAsset } from '../../services/page-assets.service';
import { StylePanel } from './StylePanel';
import { useViewRender } from '../../context/ViewRenderContext';

interface PropertyPanelProps {
  block: Block;
  onUpdateBlock: (updatedBlock: Block) => void;
  onDeleteBlock: () => void;
  teamId: string;
  organizationId?: string;
}

export const PropertyPanel: React.FC<PropertyPanelProps> = ({
  block,
  onUpdateBlock,
  onDeleteBlock,
  teamId,
  organizationId,
}) => {
  const [localProps, setLocalProps] = useState(block.props);
  const [localStyle, setLocalStyle] = useState<Style>(block.style || {});
  const [isExpanded, setIsExpanded] = useState(true);
  const [isStyleExpanded, setIsStyleExpanded] = useState(false);
  const [mediaDialogOpen, setMediaDialogOpen] = useState(false);
  const [mediaDialogField, setMediaDialogField] = useState<string>('');
  const [mediaDialogType, setMediaDialogType] = useState<'image' | 'video' | 'all'>('all');

  const componentMeta = ComponentRegistry[block.type];
  const schema = componentMeta?.schema;

  // For the Chat block, enumerate the OTHER content query/pick blocks in the
  // current view so the author can bind the chat's scope to a specific block.
  const { pageDoc } = useViewRender();
  const contentBlocks = React.useMemo(() => {
    const out: Array<{ id: string; label: string }> = [];
    (pageDoc?.layout ?? []).forEach((s) =>
      s.rows.forEach((r) =>
        r.columns.forEach((c) =>
          c.blocks.forEach((b) => {
            if (b.id === block.id) return;
            const t = b.type as string;
            const pos = out.length + 1; // 1-based position among content blocks (document order)
            if (t === 'ViewContentQuery' || t === 'ContentQuery') {
              const q = typeof b.props?.['searchQuery'] === 'string' ? (b.props['searchQuery'] as string).trim() : '';
              out.push({ id: b.id, label: q ? `${pos}. Search results: “${q.slice(0, 40)}”` : `${pos}. Search results` });
            } else if (t === 'ViewContentPick' || t === 'ContentPick') {
              const n = Array.isArray(b.props?.['contentIds']) ? (b.props!['contentIds'] as string[]).length : 0;
              out.push({ id: b.id, label: `${pos}. Hand-picked content (${n} ${n === 1 ? 'item' : 'items'})` });
            }
          }),
        ),
      ),
    );
    return out;
  }, [pageDoc, block.id]);

  // Sync local state when block changes
  useEffect(() => {
    setLocalProps(block.props);
    setLocalStyle(block.style || {});
  }, [block.id, block.props, block.style]); // Re-sync when block changes

  const handlePropChange = useCallback((propName: string, value: any) => {
    const updatedProps = { ...localProps, [propName]: value };
    setLocalProps(updatedProps);
    
    // Update block immediately for live preview
    const updatedBlock = { ...block, props: updatedProps, style: localStyle };
    onUpdateBlock(updatedBlock);
  }, [localProps, localStyle, block, onUpdateBlock]);

  const handleStyleChange = useCallback((newStyle: Style) => {
    setLocalStyle(newStyle);
    
    // Update block immediately for live preview
    const updatedBlock = { ...block, props: localProps, style: newStyle };
    onUpdateBlock(updatedBlock);
  }, [localProps, block, onUpdateBlock]);

  const handleMediaFieldClick = (fieldName: string, assetType: 'image' | 'video' | 'all') => {
    setMediaDialogField(fieldName);
    setMediaDialogType(assetType);
    setMediaDialogOpen(true);
  };

  const handleMediaSelect = (asset: PageAsset) => {
    if (mediaDialogField && asset.url) {
      handlePropChange(mediaDialogField, asset.url);
    }
    setMediaDialogOpen(false);
    setMediaDialogField('');
  };

  const isMediaField = (fieldName: string, componentType: ComponentType): boolean => {
    if (componentType === 'Image' && fieldName === 'src') return true;
    // if (componentType === 'Video' && fieldName === 'src') return true;
    return false;
  };

  const getMediaAssetType = (fieldName: string, componentType: ComponentType): 'image' | 'video' | 'all' => {
    if (componentType === 'Image' && fieldName === 'src') return 'image';
    // if (componentType === 'Video' && fieldName === 'src') return 'video';
    return 'all';
  };

  if (!schema) {
    return (
      <Paper sx={{ width: 320, p: 2, borderRadius: 0, borderLeft: 1, borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>
          Properties
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="body2" color="error">
          No schema defined for {block.type}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ 
      width: 320, 
      borderRadius: 0, 
      borderLeft: 1, 
      borderColor: 'divider', 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden' // Add this to contain children properly
    }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>
          Properties
        </Typography>
        
        {/* Component Info */}
        <Box sx={{ mb: 2 }}>
          <Chip 
            label={componentMeta.label} 
            color="primary" 
            variant="outlined" 
            sx={{ mb: 1 }}
          />
          <Typography variant="caption" display="block" color="text.secondary">
            {componentMeta.description}
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
            ID: {block.id}
          </Typography>
        </Box>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto', 
        minHeight: 0,
        position: 'relative'
      }}>
        <Box sx={{ 
          p: 2,
          paddingBottom: 10 // Increased padding at bottom to ensure all content is reachable
        }}>
          <Accordion 
            expanded={isExpanded} 
            onChange={() => setIsExpanded(!isExpanded)} 
            sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}
            disableGutters
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 'auto' }}>
              <Typography variant="subtitle1">Component Settings</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, pt: 1 }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 2
              }}>
                {renderSchemaFields(schema, localProps, handlePropChange, block.type, handleMediaFieldClick, contentBlocks)}
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Style Settings */}
          <Accordion 
            expanded={isStyleExpanded} 
            onChange={() => setIsStyleExpanded(!isStyleExpanded)} 
            sx={{ boxShadow: 'none', '&:before': { display: 'none' }, mt: 2 }}
            disableGutters
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 'auto' }}>
              <Typography variant="subtitle1">Style Settings</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, pt: 1 }}>
              <StylePanel
                elementType="block"
                elementId={block.id}
                style={localStyle}
                onUpdateStyle={handleStyleChange}
                readonly={false}
              />
            </AccordionDetails>
          </Accordion>
        </Box>
      </Box>

      {/* Actions Footer */}
      <Box sx={{ 
        p: 2, 
        borderTop: 1, 
        borderColor: 'divider', 
        backgroundColor: 'background.paper',
        flexShrink: 0 // Prevent footer from shrinking
      }}>
        <Button
          variant="outlined"
          color="error"
          fullWidth
          onClick={onDeleteBlock}
        >
          Delete Component
        </Button>
      </Box>

      {/* Media Upload Dialog */}
      <MediaUploadDialog
        open={mediaDialogOpen}
        onClose={() => setMediaDialogOpen(false)}
        onSelect={handleMediaSelect}
        teamId={teamId}
        organizationId={organizationId}
        assetType={mediaDialogType}
        currentAssetUrl={mediaDialogField && typeof localProps[mediaDialogField] === 'string' ? localProps[mediaDialogField] : undefined}
      />
    </Paper>
  );
};

function renderSchemaFields(
  schema: z.ZodType<any>,
  props: Record<string, any>,
  onChange: (key: string, value: any) => void,
  componentType?: ComponentType,
  onMediaFieldClick?: (fieldName: string, assetType: 'image' | 'video' | 'all') => void,
  contentBlocks?: Array<{ id: string; label: string }>
): React.ReactNode[] {
  if (!(schema instanceof z.ZodObject)) {
    return [<Typography key="error" variant="caption" color="error">Unsupported schema type</Typography>];
  }

  const shape = schema.shape;
  const fields: React.ReactNode[] = [];

  // Add special message for ViewContentQuery
  if (componentType === 'ViewContentQuery') {
    fields.push(
      <Box key="query-message" sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1, mb: 2 }}>
        <Typography variant="body2" color="info.dark">
          💡 Query configuration is managed through the component's "Configure Query" button in the editor.
        </Typography>
      </Box>
    );
  }

  // Special handling for Divider component - group related properties
  if (componentType === 'Divider') {
    const dividerGroups = {
      basic: ['variant', 'orientation', 'thickness', 'color', 'backgroundColor', 'style'],
      spacing: ['marginTop', 'marginBottom', 'marginLeft', 'marginRight', 'paddingTop', 'paddingBottom', 'paddingLeft', 'paddingRight'],
      appearance: ['borderRadius', 'opacity', 'width', 'height', 'boxShadow', 'textAlign']
    };

    // Basic Properties Group
    fields.push(
      <Box key="divider-basic-group" sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
          📏 Basic Properties
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pl: 1 }}>
          {dividerGroups.basic.map(key => {
            if (!shape[key]) return null;
            const field = renderField(key, shape[key] as z.ZodType<any>, props[key], onChange, componentType, onMediaFieldClick);
            return field ? <Box key={`basic-${key}`}>{field}</Box> : null;
          })}
        </Box>
      </Box>
    );

    // Spacing Properties Group
    fields.push(
      <Box key="divider-spacing-group" sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
          📐 Spacing Controls
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pl: 1 }}>
          {dividerGroups.spacing.map(key => {
            if (!shape[key]) return null;
            const field = renderField(key, shape[key] as z.ZodType<any>, props[key], onChange, componentType, onMediaFieldClick);
            return field ? <Box key={`spacing-${key}`}>{field}</Box> : null;
          })}
        </Box>
      </Box>
    );

    // Appearance Properties Group
    fields.push(
      <Box key="divider-appearance-group" sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
          🎨 Appearance & Effects
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pl: 1 }}>
          {dividerGroups.appearance.map(key => {
            if (!shape[key]) return null;
            const field = renderField(key, shape[key] as z.ZodType<any>, props[key], onChange, componentType, onMediaFieldClick);
            return field ? <Box key={`appearance-${key}`}>{field}</Box> : null;
          })}
        </Box>
      </Box>
    );

    return fields; // Return early for Divider to use custom grouping
  }

  // Special handling for Embed component - group security fields together
  if (componentType === 'Embed') {
    const securityFields = ['allowScripts', 'allowForms', 'allowSameOrigin', 'allowPopups', 'allowModals', 'allowDownloads'];
    const securityFieldSet = new Set(securityFields);

    // Known embeddable URL patterns — no warning needed for these
    const KNOWN_EMBEDDABLE = [/youtube\.com/, /youtu\.be/, /vimeo\.com/, /player\.vimeo\.com/, /spotify\.com/, /docs\.google\.com/, /figma\.com/, /soundcloud\.com/, /canva\.com/];
    // Known blocked domains
    const KNOWN_BLOCKED = ['google.com', 'facebook.com', 'twitter.com', 'x.com', 'instagram.com', 'linkedin.com', 'reddit.com', 'amazon.com', 'netflix.com', 'tiktok.com', 'pinterest.com', 'snapchat.com', 'apple.com', 'microsoft.com', 'github.com'];

    // Render all non-security fields first
    Object.entries(shape).forEach(([key, fieldSchema]) => {
      if (securityFieldSet.has(key)) return;
      const field = renderField(key, fieldSchema as z.ZodType<any>, props[key], onChange, componentType, onMediaFieldClick);
      if (field) fields.push(<Box key={key}>{field}</Box>);

      // Inject URL advisory immediately after the src field
      if (key === 'src' && props.src) {
        const url = props.src as string;
        const isKnownEmbeddable = KNOWN_EMBEDDABLE.some(pattern => pattern.test(url));
        let hostname = '';
        try { hostname = new URL(url.startsWith('http') ? url : `https://${url}`).hostname.replace(/^www\./, ''); } catch {}
        const isKnownBlocked = KNOWN_BLOCKED.some(d => hostname === d || hostname.endsWith(`.${d}`));

        if (isKnownBlocked) {
          fields.push(
            <Box key="embed-url-warning" sx={{ p: 1.5, bgcolor: 'error.main', borderRadius: 1 }}>
              <Typography variant="caption" sx={{ color: 'white' }}>
                ⚠️ <strong>{hostname}</strong> is known to block embedding. Content will not display for viewers. Use the site's official share/embed option to get a compatible URL.
              </Typography>
            </Box>
          );
        } else if (!isKnownEmbeddable) {
          fields.push(
            <Box key="embed-url-advisory" sx={{ p: 1.5, bgcolor: 'warning.main', borderRadius: 1 }}>
              <Typography variant="caption" sx={{ color: 'white' }}>
                ℹ️ Many sites block embedding. If the content doesn't appear, use the site's official share/embed feature to get a compatible URL.
              </Typography>
            </Box>
          );
        }
      }
    });

    // Render security fields as a named group
    fields.push(
      <Box key="embed-security-group" sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
          🔒 Security
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', pl: 1 }}>
          {securityFields.map(key => {
            if (!shape[key]) return null;
            const field = renderField(key, shape[key] as z.ZodType<any>, props[key], onChange, componentType, onMediaFieldClick);
            return field ? <Box key={`security-${key}`}>{field}</Box> : null;
          })}
        </Box>
      </Box>
    );

    return fields;
  }

  Object.entries(shape).forEach(([key, fieldSchema]) => {
    // Skip certain fields for RichText component
    if (componentType === 'RichText') {
      // Skip html field - it's edited via the WYSIWYG editor
      if (key === 'html') return;
      // Skip placeholder - not essential for end users to customize
      if (key === 'placeholder') return;
    }
    
    // Skip contentIds field for ViewContentPick - it's managed by the component itself
    if (componentType === 'ViewContentPick' && key === 'contentIds') {
      return;
    }
    
    // Skip query configuration fields for ViewContentQuery - they're managed by the dialog
    if (componentType === 'ViewContentQuery' &&
        (key === 'searchQuery' || key === 'filters' || key === 'sortBy' || key === 'sortOrder')) {
      return;
    }

    // Skip scopeBlockIds for Chat - it's rendered as the dedicated multi-select
    // control below. The generic renderer would otherwise expose raw block IDs.
    if (componentType === 'Chat' && key === 'scopeBlockIds') {
      return;
    }

    const field = renderField(key, fieldSchema as z.ZodType<any>, props[key], onChange, componentType, onMediaFieldClick);
    if (field) {
      fields.push(<Box key={key}>{field}</Box>);
    }
  });

  if (componentType === 'Chat') {
    const allIds = contentBlocks?.map((b) => b.id) ?? [];
    const selected: string[] = Array.isArray(props.scopeBlockIds)
      ? (props.scopeBlockIds as string[]).filter((id) => allIds.includes(id))
      : allIds;
    if ((contentBlocks?.length ?? 0) === 0) {
      // Prepend: scope is the most important Chat setting, so it leads the panel.
      fields.unshift(
        <Box key="chat-scope-empty">
          <Typography variant="caption" color="text.secondary">
            Add a content query or pick block to this view to enable chat.
          </Typography>
        </Box>,
      );
    } else {
      const allSelected = selected.length === allIds.length;
      const labelById = new Map((contentBlocks ?? []).map((b) => [b.id, b.label]));
      // Persist explicit ids; when everything is selected, clear to undefined so
      // the chat tracks the whole view even as blocks are added/removed.
      const persist = (next: string[]) =>
        onChange('scopeBlockIds', next.length === allIds.length ? undefined : next);
      const summary =
        selected.length === 0
          ? 'None selected — chat disabled'
          : allSelected
            ? 'Whole view (all content)'
            : `${selected.length} of ${allIds.length} blocks`;
      // Prepend: scope is the most important Chat setting, so it leads the panel.
      fields.unshift(
        <Box key="chat-scope">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">Content in scope</Typography>
            <Button size="small" onClick={() => persist(allSelected ? [] : allIds)} sx={{ minWidth: 0, px: 1 }}>
              {allSelected ? 'Deselect all' : 'Select all'}
            </Button>
          </Box>
          <FormControl fullWidth size="small">
            <Select
              multiple
              displayEmpty
              value={selected}
              onChange={(e) => {
                const val = e.target.value;
                persist(typeof val === 'string' ? val.split(',') : (val as string[]));
              }}
              renderValue={() => summary}
            >
              {(contentBlocks ?? []).map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  <Checkbox size="small" checked={selected.includes(b.id)} />
                  <ListItemText primary={labelById.get(b.id) ?? b.label} />
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {selected.length === 0
                ? 'Select at least one block so the chat has content to answer from.'
                : 'Chat answers are drawn only from the selected content.'}
            </FormHelperText>
          </FormControl>
        </Box>,
      );
    }
  }

  return fields;
}

function renderField(
  key: string,
  fieldSchema: z.ZodType<any>,
  value: any,
  onChange: (key: string, value: any) => void,
  componentType?: ComponentType,
  onMediaFieldClick?: (fieldName: string, assetType: 'image' | 'video' | 'all') => void
): React.ReactNode {
  const label = getFieldLabel(key);
  const fieldValue = value ?? getDefaultValue(fieldSchema);

  // Handle optional and default fields
  let innerSchema = fieldSchema;
  if (fieldSchema instanceof z.ZodOptional) {
    innerSchema = fieldSchema.unwrap();
  }
  if (fieldSchema instanceof z.ZodDefault) {
    innerSchema = fieldSchema._def.innerType;
  }

  // String fields
  if (innerSchema instanceof z.ZodString) {
    const enumValues = (innerSchema as any)._def.checks?.find((c: any) => c.kind === 'enum')?.values;
    
    if (enumValues && Array.isArray(enumValues)) {
      // Enum select
      return (
        <FormControl fullWidth size="small">
          <InputLabel>{label}</InputLabel>
          <Select
            value={fieldValue || ''}
            label={label}
            onChange={(e) => onChange(key, e.target.value)}
          >
            {enumValues.map((option: string) => (
              <MenuItem key={option} value={option}>
                {formatFieldLabel(option)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    // Check if this is a color field
    const isColorField = key.toLowerCase().includes('color');
    
    if (isColorField) {
      // Helper to check if color value is transparent
      const isTransparent = !fieldValue || fieldValue === 'transparent' || fieldValue === 'rgba(0,0,0,0)' || fieldValue === 'rgba(0, 0, 0, 0)';

      // Get actual color for native color picker (can't handle transparent)
      // Use #000001 (visually black but distinguishable from transparent) so we can detect when user selects black
      const colorPickerValue = isTransparent ? '#000001' : fieldValue;

      // Predefined color palette
      const colorPalette = [
        '#000000', '#ffffff', '#e0e0e0', '#9e9e9e', '#616161',
        '#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5',
        '#2196f3', '#03a9f4', '#00bcd4', '#009688', '#4caf50',
        '#8bc34a', '#cddc39', '#ffeb3b', '#ffc107', '#ff9800',
        '#ff5722', '#795548', '#607d8b'
      ];

      return (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <TextField
              fullWidth
              size="small"
              label={label}
              value={isTransparent ? 'transparent' : fieldValue}
              onChange={(e) => onChange(key, e.target.value)}
              placeholder="transparent or #ffffff"
              helperText={getFieldDescription(key, innerSchema) || 'Enter "transparent", hex color, or use picker'}
              InputProps={{
                startAdornment: (
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: 1,
                      border: '1px solid #ccc',
                      mr: 1,
                      // Show checkerboard pattern for transparent colors
                      ...(isTransparent ? {
                        backgroundColor: '#fff',
                        backgroundImage: 'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc)',
                        backgroundSize: '8px 8px',
                        backgroundPosition: '0 0, 4px 4px'
                      } : {
                        backgroundColor: fieldValue,
                        backgroundImage: 'none'
                      })
                    }}
                  />
                )
              }}
            />
            {!isTransparent && (
              <input
                type="color"
                value={colorPickerValue}
                onChange={(e) => onChange(key, e.target.value)}
                style={{
                  width: 40,
                  height: 40,
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                  padding: 0
                }}
                title="Pick custom color"
              />
            )}
          </Box>
          {/* Quick color palette */}
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 0.5,
            p: 1,
            bgcolor: 'grey.50',
            borderRadius: 1
          }}>
            {/* Transparent option */}
            <Box
              onClick={() => onChange(key, 'transparent')}
              sx={{
                width: 24,
                height: 24,
                borderRadius: 1,
                border: isTransparent ? '2px solid primary.main' : '1px solid #ccc',
                cursor: 'pointer',
                backgroundImage: 'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc)',
                backgroundSize: '8px 8px',
                backgroundPosition: '0 0, 4px 4px',
                '&:hover': { transform: 'scale(1.1)' }
              }}
              title="Transparent"
            />
            {colorPalette.map((color) => (
              <Box
                key={color}
                onClick={() => onChange(key, color)}
                sx={{
                  width: 24,
                  height: 24,
                  backgroundColor: color,
                  borderRadius: 1,
                  border: fieldValue === color ? '2px solid primary.main' : '1px solid #ccc',
                  cursor: 'pointer',
                  '&:hover': { transform: 'scale(1.1)' }
                }}
                title={color}
              />
            ))}
          </Box>
        </Box>
      );
    }

    // Check if this is a media field (Image src)
    const isMediaField = componentType && 
                         ((componentType === 'Image' && key === 'src'));
                          // (componentType === 'Video' && key === 'src'));
    
    if (isMediaField && onMediaFieldClick) {
      const assetType = componentType === 'Image' ? 'image' : 'all';
                       // componentType === 'Video' ? 'video' : 'all';
      const MediaIcon = ImageIcon; // componentType === 'Image' ? ImageIcon : VideoIcon;
      
      return (
        <Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              size="small"
              label={label}
              value={fieldValue || ''}
              onChange={(e) => onChange(key, e.target.value)}
              helperText={getFieldDescription(key, innerSchema)}
              sx={{ flex: 1 }}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={() => onMediaFieldClick(key, assetType)}
              startIcon={<MediaIcon />}
              sx={{ 
                minWidth: 'auto', 
                px: 1, 
                height: '40px', // Match TextField height
                mt: 0
              }}
            >
              Browse
            </Button>
          </Box>
        </Box>
      );
    }

    // Multiline for certain field names
    const isMultiline = key.toLowerCase().includes('html') || 
                       key.toLowerCase().includes('content') || 
                       key.toLowerCase().includes('description');

    return (
      <TextField
        fullWidth
        size="small"
        label={label}
        value={fieldValue || ''}
        multiline={isMultiline}
        rows={isMultiline ? 3 : 1}
        onChange={(e) => onChange(key, e.target.value)}
        helperText={getFieldDescription(key, innerSchema)}
      />
    );
  }

  // Number fields
  if (innerSchema instanceof z.ZodNumber) {
    // Extract min and max constraints from Zod schema
    const checks = (innerSchema as any)._def.checks || [];
    let min: number | undefined;
    let max: number | undefined;
    let step = 1;
    
    for (const check of checks) {
      if (check.kind === 'min') {
        min = check.value;
      } else if (check.kind === 'max') {
        max = check.value;
      }
    }
    
    // Special handling for opacity - render as slider
    if (key.toLowerCase() === 'opacity' && min !== undefined && max !== undefined) {
      return (
        <Box>
          <Typography variant="body2" fontWeight="medium" gutterBottom>
            {label}: {fieldValue ?? 1}
          </Typography>
          <Slider
            value={fieldValue ?? 1}
            onChange={(_, value) => onChange(key, value as number)}
            min={min}
            max={max}
            step={0.1}
            marks={[
              { value: min, label: `${Math.round(min * 100)}%` },
              { value: (min + max) / 2, label: `${Math.round(((min + max) / 2) * 100)}%` },
              { value: max, label: `${Math.round(max * 100)}%` }
            ]}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
          />
          <Typography variant="caption" color="text.secondary">
            {getFieldDescription(key, innerSchema)}
          </Typography>
        </Box>
      );
    }
    
    return (
      <TextField
        fullWidth
        size="small"
        label={label}
        type="number"
        value={fieldValue ?? ''}
        onChange={(e) => onChange(key, e.target.value ? Number(e.target.value) : undefined)}
        helperText={getFieldDescription(key, innerSchema)}
        inputProps={{
          min: min,
          max: max,
          step: step
        }}
      />
    );
  }

  // Boolean fields
  if (innerSchema instanceof z.ZodBoolean) {
    return (
      <FormControlLabel
        control={
          <Switch
            checked={Boolean(fieldValue)}
            onChange={(e) => onChange(key, e.target.checked)}
          />
        }
        label={label}
      />
    );
  }

  // Array fields
  if (innerSchema instanceof z.ZodArray) {
    const arrayValue = Array.isArray(fieldValue) ? fieldValue : [];
    
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" fontWeight="medium">{label}</Typography>
          <IconButton
            size="small"
            onClick={() => onChange(key, [...arrayValue, getDefaultArrayItem(innerSchema.element)])}
          >
            <AddIcon />
          </IconButton>
        </Box>
        
        {arrayValue.map((item: any, index: number) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <TextField
              size="small"
              fullWidth
              value={item || ''}
              onChange={(e) => {
                const newArray = [...arrayValue];
                newArray[index] = e.target.value;
                onChange(key, newArray);
              }}
              placeholder={`${label} ${index + 1}`}
            />
            <IconButton
              size="small"
              onClick={() => {
                const newArray = arrayValue.filter((_, i) => i !== index);
                onChange(key, newArray);
              }}
            >
              <RemoveIcon />
            </IconButton>
          </Box>
        ))}
        
        {arrayValue.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            No items. Click + to add.
          </Typography>
        )}
      </Box>
    );
  }

  // Enum fields (z.enum) — binary enums render as a segmented toggle since
  // they're structurally a two-state switch; everything else uses a dropdown.
  if (innerSchema instanceof z.ZodEnum) {
    const options = innerSchema._def.values;

    if (options.length === 2) {
      const current = fieldValue ?? options[0];
      return (
        <Box>
          <Typography variant="body2" sx={{ mb: 0.5 }}>{label}</Typography>
          <ToggleButtonGroup
            value={current}
            exclusive
            size="small"
            fullWidth
            onChange={(_, next) => {
              if (next !== null) onChange(key, next);
            }}
          >
            {options.map((option: string) => (
              <ToggleButton key={option} value={option}>
                {formatFieldLabel(option)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      );
    }

    return (
      <FormControl fullWidth size="small">
        <InputLabel>{label}</InputLabel>
        <Select
          value={fieldValue || ''}
          label={label}
          onChange={(e) => onChange(key, e.target.value)}
        >
          {options.map((option: string) => (
            <MenuItem key={option} value={option}>
              {formatFieldLabel(option)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  // Object fields (simplified)
  if (innerSchema instanceof z.ZodObject) {
    return (
      <Box>
        <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
          {label}
        </Typography>
        <TextField
          fullWidth
          size="small"
          multiline
          rows={2}
          value={JSON.stringify(fieldValue || {}, null, 2)}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value);
              onChange(key, parsed);
            } catch {
              // Invalid JSON, don't update
            }
          }}
          helperText="JSON format"
        />
      </Box>
    );
  }

  // Fallback for unknown types
  return (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={String(fieldValue || '')}
      onChange={(e) => onChange(key, e.target.value)}
      helperText={`Type: ${innerSchema.constructor.name}`}
    />
  );
}

function formatFieldLabel(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
}

// Per-field label overrides for cases where the schema key doesn't format
// into a user-friendly label (e.g. `href` → "Optional Hyperlink").
const FIELD_LABEL_OVERRIDES: Record<string, string> = {
  href: 'Optional Hyperlink',
};

function getFieldLabel(key: string): string {
  return FIELD_LABEL_OVERRIDES[key] ?? formatFieldLabel(key);
}

function getDefaultValue(schema: z.ZodType<any>): any {
  if (schema instanceof z.ZodOptional) {
    return getDefaultValue(schema.unwrap());
  }
  
  if (schema instanceof z.ZodString) return '';
  if (schema instanceof z.ZodNumber) return 0;
  if (schema instanceof z.ZodBoolean) return false;
  if (schema instanceof z.ZodArray) return [];
  if (schema instanceof z.ZodObject) return {};
  
  return undefined;
}

function getDefaultArrayItem(elementSchema: z.ZodType<any>): any {
  return getDefaultValue(elementSchema);
}

function getFieldDescription(key: string, schema: z.ZodType<any>): string {
  // Check if schema has a description
  const schemaDescription = (schema as any)._def?.description;
  if (schemaDescription) {
    return schemaDescription;
  }
  
  // Add helpful descriptions based on field name
  const descriptions: Record<string, string> = {
    html: 'HTML content for rich text',
    src: 'Enter the full URL (e.g., https://youtube.com/embed/...)',
    url: 'Web URL',
    href: 'Link destination',
    width: 'Width in pixels, percentage, or auto (e.g., 100%, 300px, auto)',
    height: 'Height in pixels, percentage, or auto (e.g., 100px, auto)',
    containerHeight: 'Fixed height for scrolling (e.g., 400px, 50vh, calc(100vh - 200px))',
    alt: 'Alternative text for accessibility',
    placeholder: 'Placeholder text',
    value: 'Current value',
    label: 'Display label',
    text: 'Button text',
    title: 'Title or heading',
    variant: 'Visual style variant',
    color: 'Color theme',
    size: 'Size option',
    allowFullscreen: 'Allow embedded content to go fullscreen',
    loading: 'When to load the embedded content',
    allowScripts: 'Allow JavaScript',
    allowForms: 'Allow form submission',
    allowSameOrigin: 'Allow cookie and storage access',
    allowPopups: 'Allow opening new windows',
    allowModals: 'Allow dialog boxes',
    allowDownloads: 'Allow file downloads',
    // Divider-specific descriptions
    orientation: 'Direction of the divider line',
    thickness: 'Thickness of the divider line in pixels',
    style: 'Line style (solid, dashed, dotted, etc.)',
    backgroundColor: 'Background color of the divider container',
    marginTop: 'Top margin in pixels',
    marginBottom: 'Bottom margin in pixels',
    marginLeft: 'Left margin in pixels',
    marginRight: 'Right margin in pixels',
    paddingTop: 'Top padding in pixels',
    paddingBottom: 'Bottom padding in pixels',
    paddingLeft: 'Left padding in pixels',
    paddingRight: 'Right padding in pixels',
    borderRadius: 'Rounded corners in pixels',
    opacity: 'Transparency (1.0 = opaque, 0.1 = very transparent)',
    boxShadow: 'CSS box-shadow effect (e.g., 0 2px 4px rgba(0,0,0,0.1))',
    textAlign: 'Alignment of the divider within its container',
  };

  return descriptions[key.toLowerCase()] || '';
}

export default PropertyPanel;